import {
  exportToExcelMultiSheet,
  generateMultiSheetTemplate,
  ExcelColumn,
} from "./excelUtils";
import { normalizeBackfillType } from "./backfillTypes";
import {
  FOUL_EO_CONC_BED_ITEMS,
  FOUL_EO_ITEMS,
  FOUL_REDUNDANT_SEWER_ITEMS,
} from "./foulSummaryUtils";
import { PUMP_STATION_ROWS, RISING_MAIN_ROWS, BuildUpRowDef } from "./foulBuildUpRows";
import {
  Manhole,
  SewerPipeRun,
  LateralConnection,
  FoulBuildUpRowData,
  FoulRoadConstConfig,
  PumpChamberConfig,
} from "../types/tender.types";

export interface ManholeScheduleConfig {
  ladders: 'N/A' | 'Stainless' | 'Galvanised';
  safetyChains: 'N/A' | 'Stainless' | 'Galvanised';
}

export interface FoulSewersExcelData {
  manholes: Manhole[];
  pipeRuns: SewerPipeRun[];
  laterals: LateralConnection[];
  buildUp?: Record<string, FoulBuildUpRowData>;
  roadConstConfig?: FoulRoadConstConfig;
  manholeScheduleConfig?: ManholeScheduleConfig;
  pumpChamberConfig?: PumpChamberConfig;
  // Per-section title text read off the section header ("PUMP CHAMBER - North") — header row id → text.
  pumpStationSectionTitles?: Record<string, string>;
  // Manual-input quantities read from the 'Foul Summary' sheet — see
  // parseFoulSummarySheet below for which rows these can come from.
  eoQtys?: Record<string, number>;
}

// Column definitions for manholes
const manholeColumns: ExcelColumn[] = [
  { key: "ref", title: "MH Ref" },
  {
    key: "coverLevel",
    title: "Cover Level (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "invertLevel",
    title: "Invert Level (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depth",
    title: "Depth (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depthCategory",
    title: "Depth Category",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "diameter", title: "Diameter" },
  { key: "type", title: "Type" },
];

// Column definitions for pipe runs
const pipeRunColumns: ExcelColumn[] = [
  { key: "fromMH", title: "From MH" },
  { key: "toMH", title: "To MH" },
  {
    key: "length",
    title: "Length (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "diameter", title: "Pipe Diameter" },
  { key: "pipeType", title: "Pipe Type" },
  { key: "backfillType", title: "Backfill Type" },
  {
    key: "depthFrom",
    title: "Depth From (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depthTo",
    title: "Depth To (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "averageDepth",
    title: "Avg Depth (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depthCategory",
    title: "MH Depth Category",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "runDepthCategory",
    title: "Run Depth Category",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "asDugBackfill",
    title: "As Dug Backfill",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "rockers", title: "Rockers", render: (value) => Number(value || 0) },
];

// Column definitions for lateral connections
const lateralColumns: ExcelColumn[] = [
  { key: "toManhole", title: "Ref" },
  {
    key: "length",
    title: "Length (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "pipeDiameter", title: "Pipe Diameter" },
  { key: "manholeDiameter", title: "Manhole Diameter" },
  {
    key: "backdrop",
    title: "Backdrop",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depth",
    title: "Depth (m)",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "requiresFirstOff", title: "Requires First Off" },
  { key: "firstOffType", title: "First Off Type" },
  {
    key: "lateralManholeDepthCategory",
    title: "Lateral MH Depth Category",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "depthOffMainRun",
    title: "Depth Off Main Run",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "averageDepth",
    title: "Average Depth",
    render: (value) => Number(value || 0).toFixed(3),
  },
  {
    key: "avgDepthCategory",
    title: "Avg Depth Category",
    render: (value) => Number(value || 0).toFixed(3),
  },
  { key: "rockers", title: "Rockers", render: (value) => Number(value || 0) },
];

/**
 * Export foul sewers data (manholes, pipe runs, laterals) to Excel
 */
export const exportFoulSewersToExcel = async (
  data: FoulSewersExcelData,
  projectName: string = "Foul Sewers"
): Promise<void> => {
  const timestamp = new Date().toISOString().split("T")[0];
  const filename = `${projectName}_FoulSewers_${timestamp}`;

  const sheets = [
    {
      name: "Manholes",
      columns: manholeColumns,
      data: data.manholes || [],
    },
    {
      name: "Pipe Runs",
      columns: pipeRunColumns,
      data: data.pipeRuns || [],
    },
    {
      name: "Laterals",
      columns: lateralColumns,
      data: data.laterals || [],
    },
  ];

  // Filter out empty sheets
  const nonEmptySheets = sheets.filter((sheet) => sheet.data.length > 0);

  if (nonEmptySheets.length === 0) {
    throw new Error("No data to export");
  }

  await exportToExcelMultiSheet({
    filename,
    includeBranding: true,
    projectName: projectName,
    sheets: nonEmptySheets,
  });
};

/**
 * Generate Excel template for foul sewers import
 */
export const generateFoulSewersTemplate = async (
  projectName: string = "FoulSewers"
): Promise<void> => {
  const timestamp = new Date().toISOString().split("T")[0];
  const filename = `${projectName}_FoulSewers_Template_${timestamp}`;

  const sheets = [
    { name: "Manholes", columns: manholeColumns },
    { name: "Pipe Runs", columns: pipeRunColumns },
    { name: "Laterals", columns: lateralColumns },
  ];

  await generateMultiSheetTemplate(filename, sheets);
};

/** Convert raw rows (array-of-arrays) + headers into objects keyed by header. */
const rowsToObjects = (
  raw: unknown[][],
  headers: string[]
): Record<string, unknown>[] =>
  raw
    .filter((r) => r.some((c) => c !== null && c !== undefined && c !== ''))
    .map((r) =>
      Object.fromEntries(
        headers.map((h, i) => [h.trim(), r[i] ?? null])
      )
    );

/** Pick first non-null / non-empty value from a list of possible column names. */
const col = (row: Record<string, unknown>, ...names: string[]): unknown => {
  for (const name of names) {
    const v = row[name];
    if (v !== null && v !== undefined && String(v).trim() !== '') return v;
  }
  return null;
};

const toNum = (v: unknown, fallback = 0): number => {
  const n = Number(v);
  return isNaN(n) ? fallback : n;
};

const toStr = (v: unknown, fallback = ''): string =>
  v !== null && v !== undefined ? String(v).trim() : fallback;

const ensureDiameterUnit = (v: unknown, def = '1200mm'): string => {
  if (!v) return def;
  const s = String(v).trim();
  return /^\d+$/.test(s) ? `${s}mm` : s;
};

// ─── Normalizers ─────────────────────────────────────────────────────────────

const isNaOrEmpty = (v: unknown): boolean => {
  if (v === null || v === undefined) return true;
  const s = String(v).trim();
  return s === '' || s === '#N/A' || s.toLowerCase() === 'n/a';
};

// Manhole references: Excel formula returns "0" when the cell is blank
const isEmptyManholeRef = (v: unknown): boolean =>
  isNaOrEmpty(v) || String(v).trim() === '0';

const normalizePipeType = (v: unknown): string => {
  const s = String(v ?? '').toLowerCase().trim();
  if (s.includes('ultra')) return 'ultra';
  if (s.includes('rigi')) return 'rigi';
  if (s.includes('conc')) return 'concrete';
  return 'clay';
};

// ─── Transformer: 'Foul Manhole Schedule' ────────────────────────────────────
// Direct Excel column → app field mapping:
//   Excel "Manhole"                        → ref
//   Excel "Type"                           → type  (foul/storm/both)
//   Excel "Easi / Trad"                    → easiTrad
//   Excel "Diam"                           → diameter
//   Excel "Cover"                          → coverLevel
//   Excel "Invert"                         → invertLevel
//   Excel "Depth To" / "Depth"             → recalculated from cover - invert
//   Excel "Ladders if not incl..."         → ladders
//   Excel "Safety Chains"                  → safetyChains
//   Excel "Backdrop"                       → backdrop
//   Excel "Muck stopper"                   → muckStopper
//   Excel "AMPS"                           → amps

const resolveDropdown = (v: unknown): 'N/A' | 'Stainless' | 'Galvanised' | undefined => {
  const s = String(v ?? '').trim();
  if (s === 'N/A' || s === 'n/a') return 'N/A';
  if (s.toLowerCase().startsWith('stain')) return 'Stainless';
  if (s.toLowerCase().startsWith('galv')) return 'Galvanised';
  return undefined;
};


const resolveEasiTrad = (v: unknown): 'easi' | 'trad' | undefined => {
  const s = String(v ?? '').toLowerCase().trim();
  if (s.startsWith('e')) return 'easi';
  if (s.startsWith('t')) return 'trad';
  return undefined;
};

const transformFoulManholeSchedule = (
  rows: Record<string, unknown>[]
): Manhole[] =>
  rows
    .filter((r) => col(r, 'Manhole') !== null || col(r, 'Type') !== null)
    .map((r, i) => ({
      id: `imp_mh_${i}`,
      ref:           toStr(col(r, 'Manhole'), `MH${i + 1}`),
      type:          toStr(col(r, 'Type')),
      easiTrad:      resolveEasiTrad(col(r, 'Easi / Trad')),
      diameter:      ensureDiameterUnit(col(r, 'Diam'), '1200mm'),
      coverLevel:    toNum(col(r, 'Cover')),
      invertLevel:   toNum(col(r, 'Invert')),
      // R&S Excel col header is "Depth To" (sub-heading "Invert"); generated template uses "Depth (m)"
      depth:         toNum(col(r, 'Depth To', 'Depth (m)')),
      // R&S Excel col header is "Depth " (trimmed → "Depth"); generated template uses "Depth Category"
      depthCategory: toNum(col(r, 'Depth Category', 'Depth')),
      ladders:       toNum(col(r, 'Ladders if not incl in chamber price', 'Ladders (m)')),
      safetyChains:  toNum(col(r, 'Safety Chains')),
      backdrop:      toNum(col(r, 'Backdrop')),
      muckStopper:   toNum(col(r, 'Muck stopper')),
      amps:          toNum(col(r, 'AMPS')),
    }));

// ─── Transformer: 'Foul Take off' — Main Sewer Runs ─────────────────────────

// A row with `_hasMain: false` (set by the caller's row-splitting loop) has no
// Main Sewer Run on this physical Excel row — it exists purely to keep index
// alignment with a House Lateral on the same row. Identity/classification
// fields (ref, MH refs, diameter, pipeType, backfillType, junctionSize) are
// blanked instead of falling through to a non-blank default, which would
// otherwise seed fake values that can falsely match a real run's
// branch-diameter comparisons in Foul Summary calcs. Depth/quantity columns
// are still read as-is from the row: the R&S sheet's derived-depth formulas
// (Avg Depth Category, etc.) commonly stay populated even when the MH ref
// cells on that row are blank.
const transformMainSewerRuns = (rows: Record<string, unknown>[]): SewerPipeRun[] =>
  rows.map((r, i) => {
    const hasMain = Boolean(r['_hasMain']);
    return {
      id:                 `imp_run_${i}`,
      ref:                hasMain ? toStr(col(r, 'Run ref')) : '',
      fromMH:             hasMain ? toStr(col(r, 'Manhole From')) : '',
      toMH:               hasMain ? toStr(col(r, 'Manhole To')) : '',
      length:             toNum(col(r, 'Run Length (m)', 'Run Length')),
      // No fallback default even when hasMain — a real run with a blank Pipe Ø
      // cell should stay blank, not silently become "150mm" (same fix already
      // applied to House Laterals' pipeDiameter below, and to Storm's
      // equivalent Main Sewer Run diameter).
      diameter:           hasMain ? ensureDiameterUnit(col(r, 'Pipe Ø (mm)', 'Pipe Ø', 'Pipe O (mm)'), '') : '',
      pipeType:           hasMain ? normalizePipeType(col(r, 'Clay, Conc, Ultra, Rigi', 'Clay/Conc/Ultra/Rigi')) : '',
      backfillType:       hasMain ? normalizeBackfillType(col(r, 'Backfill')) : undefined,
      roadConstDepth:     toNum(col(r, 'Road Const Depth (0.45)', 'Road Const Depth'), hasMain ? 0.45 : 0),
      // R&S Excel has "Depth From" / "Depth To"; generated template uses "(m)" suffix
      depthFrom:          toNum(col(r, 'Depth From (m)', 'Depth From')),
      depthTo:            toNum(col(r, 'Depth To (m)', 'Depth To')),
      averageDepth:       toNum(col(r, 'Avg Depth (m)', 'Avg Depth')),
      depthCategory:      0,
      // R&S Excel has "Avg Depth Category"; generated template uses "Run Depth Category"
      runDepthCategory:   toNum(col(r, 'Avg Depth Category', 'Run Depth Category')),
      rockers:            toNum(col(r, 'Rockers (nr)', 'Rockers')),
      concreteProtection: toNum(col(r, 'Concrete Protection (m, if App)', 'Concrete Protection')),
      junctionSize:       hasMain ? toStr(col(r, 'Junction size', 'Junction Size')) : '',
      junctions:          toNum(col(r, 'Junctions (Nr)', 'Junctions')),
    };
  });

// ─── Transformer: 'Foul Take off' — House Laterals ──────────────────────────

// `_hasLateral: false` means this row has no real lateral connection (no
// Manhole Ref) — every field is blanked/zeroed rather than importing any
// data for it, even the demarc/avg-depth columns that derive from the paired
// Main Sewer Run's depth category via an Excel formula and would otherwise
// stay populated regardless of whether this row has a lateral connection.
const transformHouseLaterals = (rows: Record<string, unknown>[]): LateralConnection[] =>
  rows.map((r, i) => {
    const hasLateral = Boolean(r['_hasLateral']);
    if (!hasLateral) {
      return {
        id:                  `imp_lat_${i}`,
        ref:                 '',
        connectionType:      'direct_to_manhole' as const,
        toManhole:           '',
        junctionOffRun:      0,
        pipeType:            '',
        pipeDiameter:        '',
        length:              0,
        demarcCoverLevel:    0,
        demarcInvertLevel:   0,
        demarcDepth:         0,
        demarcDepthCategory: 0,
        averageDepth:        0,
        avgDepthCategory:    0,
        nme:                 0,
        count1200:           0,
        backfillType:        undefined,
        manholeDiameter:     '',
        backdrop:            0,
        depth:               0,
        requiresFirstOff:    false,
        rockers:             0,
      };
    }
    return {
      id:                  `imp_lat_${i}`,
      ref:                 `L${i + 1}`,
      connectionType:      'direct_to_manhole' as const,
      toManhole:           toStr(col(r, 'Manhole Ref (mm)', 'Manhole Ref')),
      junctionOffRun:      toNum(col(r, 'Junction off run (Nr)', 'Junction off run')),
      pipeType:            normalizePipeType(col(r, 'Clay, Conc, Ultra, Rigi', 'Clay/Conc/Ultra/Rigi')),
      pipeDiameter:        ensureDiameterUnit(col(r, 'Pipe Ø (mm)', 'Pipe Ø', 'Pipe O (mm)'), ''),
      length:              toNum(col(r, 'Length (m)', 'Length')),
      demarcCoverLevel:    toNum(col(r, 'Demarc Cover (m)', 'Demarc Cover')),
      demarcInvertLevel:   toNum(col(r, 'Demarc Invert (m)', 'Demarc Invert')),
      // R&S Excel has "Demarc Depth" column directly — use as-is, no recalculation
      demarcDepth:         toNum(col(r, 'Demarc Depth (m)', 'Demarc Depth')),
      // R&S Excel has "Depth Category" column; generated template uses same name
      demarcDepthCategory: toNum(col(r, 'Depth Category')),
      // R&S Excel has "Ave Run Depth"; generated template uses "Average Depth"
      averageDepth:        toNum(col(r, 'Ave Run Depth (m)', 'Ave Run Depth', 'Average Depth')),
      // R&S Excel has "Avg Run Category"; generated template uses "Avg Depth Category"
      avgDepthCategory:    toNum(col(r, 'Avg Run Category (m)', 'Avg Run Category', 'Avg Depth Category')),
      nme:                 toNum(col(r, 'NME')),
      count1200:           toNum(col(r, '1200 (Nr)', '1200')),
      backfillType:        normalizeBackfillType(col(r, '_rowBackfill', 'Backfill')),
      manholeDiameter:     '1200mm',
      backdrop:            0,
      depth:               0.6,
      requiresFirstOff:    false,
      rockers:             2,
    };
  });

// ─── shared sheet reader ──────────────────────────────────────────────────────

const readSheet = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  XLSX: any,
  ws: object,
  headerRowOffset: number,
  dataStartOffset: number
): { rows: Record<string, unknown>[] } => {
  const raw: unknown[][] = XLSX.utils.sheet_to_json(ws, {
    header: 1, defval: null, raw: false, blankrows: false, range: headerRowOffset,
  });
  if (!raw || raw.length === 0 || !raw[0]) return { rows: [] };
  const headers = (raw[0] as unknown[]).map((h) => String(h ?? '').trim());
  const rows    = rowsToObjects(raw.slice(dataStartOffset) as unknown[][], headers);
  return { rows };
};

// ─── Build-Up Import ─────────────────────────────────────────────────────────

interface BuildUpSectionDef {
  trigger: RegExp;
  /** Ordered row IDs (separators excluded — they are blank rows in Excel, skipped by XLSX). */
  rowIds: string[];
}

const PUMP_STATION_SECTION_DEFS: BuildUpSectionDef[] = [
  {
    trigger: /pumping station/i,
    rowIds: ['ps_1','ps_2','ps_3','ps_4','ps_5','ps_6','ps_7','ps_8','ps_9','ps_10','ps_11','ps_12','ps_13','ps_14','ps_15','ps_16'],
  },
  {
    trigger: /pump chamber/i,
    rowIds: ['ps_pc_rock','ps_pc_blinding','ps_pc_mesh','ps_pc_wetwell','ps_pc_cover_frame','ps_pc_ladder','ps_pc_labour','ps_pc_diamond','ps_pc_pipework','ps_pc_davit','ps_pc_penstock','ps_pc_valve','ps_pc_pump_equip'],
  },
  {
    trigger: /valve chamber/i,
    rowIds: ['ps_vc_excavate','ps_vc_ews','ps_vc_formwork','ps_vc_mesh','ps_vc_concrete','ps_vc_brick','ps_vc_diamond','ps_vc_waterstop','ps_vc_ladder','ps_vc_coverslab','ps_vc_cover_frame','ps_vc_screed','ps_vc_plinths'],
  },
  {
    trigger: /flow meter chamber/i,
    rowIds: ['ps_fm_excavate','ps_fm_ews','ps_fm_formwork','ps_fm_mesh','ps_fm_concrete','ps_fm_brick','ps_fm_diamond','ps_fm_waterstop','ps_fm_ladder','ps_fm_coverslab','ps_fm_cover_frame','ps_fm_screed','ps_fm_plinths'],
  },
  {
    trigger: /kiosk base/i,
    rowIds: ['ps_kb_excavate','ps_kb_type1','ps_kb_formwork','ps_kb_mesh','ps_kb_concrete','ps_kb_plinths','ps_kb_ducts'],
  },
  {
    trigger: /overflow storage tank/i,
    // ps_wt_sub_hdr accounts for the "WEHOLITE ATTENUATION TANK" row that follows the section header
    rowIds: ['ps_wt_sub_hdr','ps_wt_tank_dim','ps_wt_cover','ps_wt_invert','ps_wt_egl','ps_wt_post_strip','ps_wt_excavate','ps_wt_level','ps_wt_granular','ps_wt_supply','ps_wt_welding','ps_wt_carriage','ps_wt_fixed_price','ps_wt_ohp','ps_wt_offload_exc','ps_wt_offload_men','ps_wt_lp_hdr','ps_wt_install_1','ps_wt_install_2','ps_wt_surround_total','ps_wt_backfill','ps_wt_stockpile','ps_wt_foundation','ps_wt_coverslab','ps_wt_seating_rings','ps_wt_cf'],
  },
];

/** Pump Station section header row id for each editable-title section, keyed by its trigger's source. */
const PUMP_STATION_HEADER_IDS: Record<string, string> = {
  'pump chamber': 'ps_pc_hdr',
  'valve chamber': 'ps_vc_hdr',
  'flow meter chamber': 'ps_fm_hdr',
  'kiosk base': 'ps_kb_hdr',
  'overflow storage tank': 'ps_wt_hdr',
};

/**
 * Reads the user's appended section title text off the Pump Station sheet's section header rows
 * ("PUMP CHAMBER - North" → ps_pc_hdr: "North"). Fires once per section, same as parseBuildUpSheet; no dash
 * may precede the section name, so a data row like "ladder supplied - pump chamber" is never read as a title.
 */
const parsePumpStationSectionTitles = (rows: unknown[][], descCols: number[]): Record<string, string> => {
  const titles: Record<string, string> = {};
  const triggered = new Set<number>();
  for (const row of rows) {
    let desc = '';
    for (const col of descCols) {
      const v = String(row[col] ?? '').trim();
      if (v) { desc = v; break; }
    }
    if (!desc || isColumnHeaderRow(desc)) continue;
    const idx = PUMP_STATION_SECTION_DEFS.findIndex((sd, i) => sd.trigger.test(desc) && !triggered.has(i));
    if (idx === -1) continue;
    triggered.add(idx);
    const def = PUMP_STATION_SECTION_DEFS[idx];
    const headerId = PUMP_STATION_HEADER_IDS[def.trigger.source];
    if (!headerId) continue;
    const m = desc.match(new RegExp(`^[^-]*?(?:${def.trigger.source})[^-]*?\\s-\\s+(.+)$`, 'i'));
    if (m && m[1].trim()) titles[headerId] = m[1].trim();
  }
  return titles;
};

const RISING_MAIN_SECTION_DEFS: BuildUpSectionDef[] = [
  {
    trigger: /rising main/i,
    // rm_hdr1 accounts for "Excavate for, supply & lay..." header row in the sheet
    rowIds: ['rm_hdr1','rm_1','rm_2','rm_3','rm_4','rm_5','rm_6','rm_7','rm_8','rm_9','rm_10','rm_11','rm_12','rm_13','rm_14','rm_15','rm_16','rm_17','rm_18','rm_19','rm_20','rm_21','rm_22','rm_23','rm_24','rm_25','rm_26','rm_27','rm_28','rm_29','rm_30','rm_31','rm_32','rm_33','rm_34','rm_35','rm_36','rm_37','rm_38','rm_39','rm_40','rm_41','rm_42','rm_43','rm_44','rm_45','rm_46'],
  },
];

/** Skip rows that are clearly column-header rows, not data rows. */
const isColumnHeaderRow = (desc: string): boolean =>
  /^(quant|description|unit|evaluate code|evaluate)/i.test(desc);

// id → description lookup, built from the same row definitions the take-off's
// own Build Up tab renders (foulBuildUpRows.ts) — used by parseBuildUpSheet
// below to match sheet rows by their actual item text rather than by row
// position (positional matching broke badly on the 'Pump Station' sheet: the
// "PUMPING STATION" section has several reference-value rows — Cover level,
// Const Th, EGL, "Pump Compound Area as per..." and its Length/Width/Area
// sub-items — sitting between the section header and its first real item row,
// which aren't part of PUMP_STATION_ROWS at all. Counting them as positional
// data rows shifted every subsequent item's Nth-position off by that many
// rows, so none of the section's real column-G quantities landed on the
// correct id).
const descByIdOf = (defs: BuildUpRowDef[]): Record<string, string> =>
  Object.fromEntries(defs.filter((r) => !r.isSeparator).map((r) => [r.id, r.description]));

const PUMP_STATION_DESC_BY_ID = descByIdOf(PUMP_STATION_ROWS);
const RISING_MAIN_DESC_BY_ID = descByIdOf(RISING_MAIN_ROWS);

// Normalizes both the sheet's actual row text and this app's row-definition
// text to the same comparable form. "Wet Well 2100mmØ 4.50m deep" embeds
// project-specific dimensions that vary per tender, so it's canonicalized to
// a fixed key rather than compared verbatim against the (placeholder) "Wet
// Well mmØ 0.00m deep" definition text. Several PUMP_STATION_ROWS descriptions
// were later reworded to placeholder text ("150mm Type 1" → "xxxmm MOT type 1
// Sub Base", etc.) — older take-off workbooks still carry the pre-reword text,
// so both forms canonicalize to the same key here, same idea as Wet Well.
const normalizeBuildUpDesc = (v: unknown): string => {
  const s = String(v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (/^wet well\b/.test(s)) return 'wet well';
  if (/^\d+\s*mm type 1$/.test(s) || /^\d+\s*mm mot type 1 sub base$/.test(s) || s === 'xxxmm mot type 1 sub base') return 'mot type 1 sub base';
  if (/^rc30 \d+\s*mm thk$/.test(s) || /^\d+\s*mm thick pav2 concrete slab$/.test(s) || s === 'xxxmm thick pav2 concrete slab') return 'pav2 concrete slab';
  if (s === 'blinding' || /^\d+\s*mm thick concrete blinding$/.test(s) || s === 'xxmm thick concrete blinding') return 'concrete blinding';
  if (/^place cover and frame supplied by .+$/.test(s) || s === 'place cover and frame supplied by _______') return 'place cover and frame - pump chamber';
  if (/^ladder supplied by .+$/.test(s) || s === 'ladder supplied by ______') return 'ladder supplied - pump chamber';
  if (/^2 layers [a-z]\d+ mesh to base$/.test(s) || /^[a-z]\d+ mesh to concrete base only$/.test(s) || s === '2 layers a393 mesh to base' || s === 'xxxx mesh to concrete base only' || s === '2 layers xxxx mesh to base') return 'mesh to concrete base';
  if (s === 'concrete base' || /^[a-z0-9]+ concrete(?: in)? base$/.test(s) || s === 'xxxxx concrete in base') return 'concrete in base';
  if (s === 'ladder' || s === 'supply / install galvanised ladders') return 'galvanised ladders';
  if (/^[a-z]\d+ mesh x 2 layers to concrete base only$/.test(s) || s === 'xxxx mesh x 2 layers to concrete base only') return 'ps10 mesh to concrete base';
  if (/^[a-z]\d+ mesh$/.test(s) || s === 'xxxx mesh') return 'kiosk base mesh';
  return s;
};

// SheetJS's `raw:false` cell formatting rounds straight off the stored double
// without first correcting for floating-point noise — unlike Excel's own
// display, which rounds to 15 significant digits before applying the number
// format. A quantity that's arithmetically 21.125 but stored as the double
// 21.124999999999996 therefore formats to "21.12" via SheetJS instead of the
// "21.13" Excel itself shows. Reading the raw numeric cell (`raw:true`) and
// applying that same 15-sig-fig correction before rounding reproduces
// Excel's displayed value.
const roundToExcelDisplay = (v: unknown, decimals = 2): number | null => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').trim());
  if (isNaN(n)) return null;
  const corrected = Number(n.toPrecision(15));
  const factor = 10 ** decimals;
  return Math.round(corrected * factor) / factor;
};

/**
 * Parse a raw sheet (array of rows) into buildUp key-value pairs, matching each
 * row against the current section's known item descriptions rather than by
 * row position. Each section trigger fires only ONCE — subsequent rows whose
 * description matches the same regex are treated as data rows, not re-triggers
 * (prevents a data row containing the section keyword, e.g. "90mm dia rising
 * main pipe", from resetting the section). A row whose description doesn't
 * match any known item for the current section is skipped rather than
 * consuming the next position, so stray reference/header rows can't shift
 * later rows onto the wrong id. A description that repeats within a section
 * (e.g. "Bed and surround - side" ×2) resolves to its ids in the order both
 * appear — first sheet occurrence → first id, second → second, etc.
 *
 * @param descCols    Single column index OR ordered array of column indices to check for
 *                    description (first non-empty wins). Pump Station uses [1, 4] (col B
 *                    for section headers, col E for item descriptions).
 * @param descById    id → description lookup for every row this sheet's sections can produce.
 * @param quantCol    0-based column index for the quantity   (default 1 = column B)
 * @param evaluateCol 0-based column index for evaluate code (-1 = not present, default)
 */
const parseBuildUpSheet = (
  rows: unknown[][],
  sectionDefs: BuildUpSectionDef[],
  descById: Record<string, string>,
  descCols: number | number[] = 0,
  quantCol = 1,
  evaluateCol = -1,
): Record<string, FoulBuildUpRowData> => {
  const result: Record<string, FoulBuildUpRowData> = {};
  const triggeredSections = new Set<number>();
  const descColArr = Array.isArray(descCols) ? descCols : [descCols];

  const getDesc = (row: unknown[]): string => {
    for (const col of descColArr) {
      const v = String(row[col] ?? '').trim();
      if (v) return v;
    }
    return '';
  };

  const buildLookup = (rowIds: string[]): Map<string, string[]> => {
    const map = new Map<string, string[]>();
    for (const id of rowIds) {
      const desc = descById[id];
      if (!desc) continue;
      const key = normalizeBuildUpDesc(desc);
      const list = map.get(key);
      if (list) list.push(id); else map.set(key, [id]);
    }
    return map;
  };

  let currentLookup: Map<string, string[]> | null = null;
  let occurrence = new Map<string, number>();

  for (const row of rows) {
    const desc = getDesc(row);
    if (!desc) continue;
    if (isColumnHeaderRow(desc)) continue;

    // Check if this row is a section header trigger — only fire each section once
    const sectionIdx = sectionDefs.findIndex(
      (sd, idx) => sd.trigger.test(desc) && !triggeredSections.has(idx)
    );
    if (sectionIdx !== -1) {
      triggeredSections.add(sectionIdx);
      currentLookup = buildLookup(sectionDefs[sectionIdx].rowIds);
      occurrence = new Map();
      continue;
    }

    if (!currentLookup) continue;

    // Match this row's description against the current section's known items
    // — an unrecognized description (a reference-value row, a sub-header,
    // etc.) is skipped without advancing anything, so it can't misalign
    // later rows onto the wrong id.
    const key = normalizeBuildUpDesc(desc);
    const ids = currentLookup.get(key);
    if (!ids) continue;
    const n = occurrence.get(key) ?? 0;
    if (n >= ids.length) continue; // more sheet rows than defined occurrences for this description
    occurrence.set(key, n + 1);
    const id = ids[n];

    const rawQuant = row[quantCol];
    if (rawQuant === null || rawQuant === undefined || rawQuant === '') continue;
    if (typeof rawQuant === 'string' && rawQuant.trim().startsWith('#')) continue; // Excel formula error
    const q = roundToExcelDisplay(rawQuant);
    if (q === null) continue;

    let evaluateCode = '';
    if (evaluateCol >= 0) {
      const rawEval = String(row[evaluateCol] ?? '').trim();
      evaluateCode = rawEval.startsWith('#') ? '' : rawEval;
    }

    result[id] = { quant: q, evaluateCode };
  }

  return result;
};

// ─── Build-Up reference-value fields (Pump Chamber's Cover Level/Invert/Sump/
//     Chamber Dia, and any other category's equivalent config fields) ───────
//
// Unlike the billable rows parseBuildUpSheet reads (quantity in col G), these
// are reference values that live in column D — the same label text (e.g.
// "Cover level") is reused across several categories on the 'Pump Station'
// sheet (Pump Chamber, Valve Chamber, Flow Meter Chamber, Overflow Storage
// Tank), each mapping to a different config field, so matching is bounded to
// rows between a category's own trigger and the next section's trigger.

const PUMP_CHAMBER_CONFIG_FIELDS: Record<string, keyof PumpChamberConfig> = {
  'cover level': 'coverLevel',
  'invert': 'invert',
  'sump': 'sump',
  'chamber dia': 'chamberDia',
};

/**
 * Reads reference-value fields (col D by default) for a single category,
 * bounded to the rows between that category's trigger row and the next
 * section trigger (from `boundaryTriggers`) — so a label shared with another
 * category (e.g. "Cover level" also appearing under Valve Chamber) is never
 * picked up outside its own section.
 */
const parseSectionReferenceValues = <TField extends string>(
  rows: unknown[][],
  sectionTrigger: RegExp,
  boundaryTriggers: RegExp[],
  descCols: number[],
  valueCol: number,
  fieldMap: Record<string, TField>,
): Partial<Record<TField, number>> => {
  const result: Partial<Record<TField, number>> = {};
  let inSection = false;

  const getDesc = (row: unknown[]): string => {
    for (const col of descCols) {
      const v = String(row[col] ?? '').trim();
      if (v) return v;
    }
    return '';
  };

  for (const row of rows) {
    const desc = getDesc(row);
    if (!desc) continue;

    if (sectionTrigger.test(desc)) {
      inSection = true;
      continue;
    }
    if (inSection && boundaryTriggers.some((t) => t !== sectionTrigger && t.test(desc))) {
      inSection = false;
      continue;
    }
    if (!inSection) continue;

    const field = fieldMap[normalizeBuildUpDesc(desc)];
    if (!field) continue;

    const raw = row[valueCol];
    if (raw === null || raw === undefined || raw === '') continue;
    const n = roundToExcelDisplay(raw, 3);
    if (n === null) continue;
    result[field] = n;
  }

  return result;
};

// ─── 'Foul Summary' sheet — manual EO/Redundant Sewer quantities ───────────
//
// This app's own Foul Summary tab calculates most rows (Drainage Runs,
// Manholes, Junctions, Lateral Connections, etc.) straight from
// pipeRuns/manholes/laterals — only EO Concrete Bed & Surround, EO Items,
// Removal of Redundant Sewer, and the Manholes section's Clegg testing row
// are freely-typed manual inputs with no formula backing them
// (foulSummaryUtils.ts's FOUL_EO_CONC_BED_ITEMS/FOUL_EO_ITEMS/
// FOUL_REDUNDANT_SEWER_ITEMS, plus the standalone 'mh-clegg' row, read via
// buildFoulSummaryData's `getManual`). The client's 'Foul Summary' sheet
// mirrors this same layout but every row's Quantity can hold either a
// manual entry or a formula result — importing blindly by row position
// would silently overwrite this app's live calculations with a stale
// snapshot. Instead, match each sheet row's description text (checked
// across every column except that row's own quantity column, since
// indentation varies by section) against these known descriptions; only a
// matched row's quantity value is applied, everything else is left
// untouched. Most sections use column I for quantity, but confirmed against
// a real client workbook, the Manholes section (e.g. Clegg testing, row
// R220) uses column R instead — override per-row via `quantCol` rather than
// assuming one column sheet-wide.
const FOUL_SUMMARY_QUANT_COL = 8; // column I — default for most sections

interface FoulSummaryManualRow {
  key: string;
  desc: string;
  /** 0-based column index for this row's quantity, if it differs from column I. */
  quantCol?: number;
  /** When true, a negative quantity is imported as-is instead of being dropped. */
  allowNegative?: boolean;
}

const FOUL_SUMMARY_MANUAL_ROWS: FoulSummaryManualRow[] = [
  ...FOUL_EO_CONC_BED_ITEMS.map((item, idx) => ({ key: `feo-conc-bed-${idx}`, desc: item.desc })),
  ...FOUL_EO_ITEMS.map((item, idx) => ({ key: `feo-item-${idx}`, desc: item.desc })),
  ...FOUL_REDUNDANT_SEWER_ITEMS.map((item, idx) => ({ key: `fredundant-${idx}`, desc: item.desc })),
  // Manholes section — row R220 of the client's 'Foul Summary' sheet; its
  // quantity sits in column R (idx 17), not column I like the rest.
  { key: 'mh-clegg', desc: "Clegg testing to sewer b'fill (500mm layers at 30m intervals)", quantCol: 17 },
  // "ADJUSTMENT FOR ROAD CONSTRUCTION GREATER THAN 450mm" → Credits — manual,
  // negative quantities entered against each of the 4 named items, same
  // column R placement as the rest of the Manholes section.
  { key: 'mh-adj-trench', desc: 'Trench Excavation', quantCol: 17, allowNegative: true },
  { key: 'mh-adj-ews', desc: 'EWS', quantCol: 17, allowNegative: true },
  { key: 'mh-adj-disposal', desc: 'Disposal', quantCol: 17, allowNegative: true },
  { key: 'mh-adj-backfill', desc: 'Backfill', quantCol: 17, allowNegative: true },
];

// Genuine wording differences (not just spacing/punctuation) confirmed
// against a real client 'Foul Summary' workbook — the source template
// itself misspells AMHEO006 as "offite" (missing the "s"), not "offsite".
// Add further confirmed variants here as they turn up, rather than
// loosening the match generally (these descriptions are matched as a
// whole string on purpose, to avoid misattributing an unrelated row).
const FOUL_SUMMARY_DESC_ALIASES: FoulSummaryManualRow[] = [
  { key: 'feo-item-0', desc: 'EO for working in offite carriageway' },
];

// Strips everything but letters/digits (not just collapsing whitespace) so
// formatting variants between this app's canonical desc strings and the
// client's actual 'Foul Summary' sheet text still match — e.g. the sheet
// spells a description as "off site" (two words) while the canonical
// string has it as one word ("offsite"); other rows could equally differ
// by a hyphen, an extra space, or punctuation. Descriptions are
// long/specific enough that this squashed comparison stays unambiguous.
const normalizeFoulSummaryDesc = (v: unknown): string =>
  String(v ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const parseFoulSummarySheet = (rows: unknown[][]): Record<string, number> => {
  const descLookup = new Map(
    [...FOUL_SUMMARY_MANUAL_ROWS, ...FOUL_SUMMARY_DESC_ALIASES].map(
      (entry) => [normalizeFoulSummaryDesc(entry.desc), entry],
    ),
  );
  const eoQtys: Record<string, number> = {};

  rows.forEach((row) => {
    let matched: FoulSummaryManualRow | undefined;
    for (let c = 0; c < row.length; c++) {
      if (c === FOUL_SUMMARY_QUANT_COL) continue;
      matched = descLookup.get(normalizeFoulSummaryDesc(row[c]));
      if (matched) break;
    }
    if (!matched) return;

    const rawQuant = row[matched.quantCol ?? FOUL_SUMMARY_QUANT_COL];
    if (rawQuant === null || rawQuant === undefined || rawQuant === '') return;
    const qStr = String(rawQuant).trim();
    if (qStr.startsWith('#')) return; // Excel formula error
    const q = parseFloat(qStr);
    if (isNaN(q)) return;
    if (q > 0 || (matched.allowNegative && q < 0)) eoQtys[matched.key] = q;
  });

  return eoQtys;
};

// ─── main import ─────────────────────────────────────────────────────────────

export const importFoulSewersFromExcel = async (
  file: File
): Promise<{ data: FoulSewersExcelData; errors: string[]; warnings: string[] }> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' });

  const importedData: FoulSewersExcelData = { manholes: [], pipeRuns: [], laterals: [], buildUp: {}, roadConstConfig: undefined };

  // ── 'Foul Manhole Schedule' — headers row 5, data from row 8 ────────────
  const mhWs = workbook.Sheets['Foul Manhole Schedule'];
  if (mhWs) {
    const { rows } = readSheet(XLSX, mhWs, 4, 3);
    importedData.manholes = transformFoulManholeSchedule(rows);

    // Read J8/K8 — global material type for ladders/safety chains (row 8, cols J=9, K=10)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const row8Raw: unknown[][] = (XLSX as any).utils.sheet_to_json(mhWs, {
      header: 1, defval: null, raw: false,
      range: { s: { r: 7, c: 0 }, e: { r: 7, c: 20 } },
    });
    const row8 = (row8Raw[0] as unknown[]) || [];
    const laddersGlobal = resolveDropdown(row8[9]);   // column J
    const chainsGlobal  = resolveDropdown(row8[10]);  // column K
    if (laddersGlobal !== undefined || chainsGlobal !== undefined) {
      importedData.manholeScheduleConfig = {
        ladders: laddersGlobal ?? 'N/A',
        safetyChains: chainsGlobal ?? 'N/A',
      };
    }
  }

  // ── 'Foul Take off' — side-by-side layout: left = Main Sewer Runs, right = House Laterals
  const toWs = workbook.Sheets['Foul Take off'];
  if (toWs) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allRows: unknown[][] = (XLSX as any).utils.sheet_to_json(toWs, {
      header: 1, defval: null, raw: false,
    });

    // The column-headers row ("Run ref", "Manhole From", ...) is found by
    // searching for its own text instead of a hardcoded absolute row index.
    // This sheet's layout has shifted by a row before (the `RS_FT_*`
    // validation key row and other header/title rows above the real headers
    // aren't a fixed count across template revisions), and a hardcoded index
    // silently reads whichever row happens to be there instead — most
    // recently the "Main Sewer Runs" section-title row, which is almost all
    // blank, so every `mainHeaders`/`lateralHeaders` entry fell back to a
    // meaningless `col_N` key and every field downstream read as empty.
    // Everything else in this block (the units row, the Road Level/Formation
    // Level/Road Construction Depth config cells, and the first data row) is
    // anchored relative to this header row rather than to more absolute
    // indices, since that internal block structure is a fixed part of the
    // sheet's own design and only the stuff *above* it has been growing.
    const headerRowIdx = allRows.findIndex(
      (row) => String((row as unknown[])?.[1] ?? '').trim().toLowerCase() === 'run ref',
    );
    if (headerRowIdx === -1) {
      console.warn("'Foul Take off': could not find the 'Run ref' header row - sheet layout may have changed.");
    }
    // headerRowIdx + 1 is a units row ("m", "mm", "Nr", ...) for every OTHER
    // column, but column G (index 6) repurposes that same row to start its
    // own 3-row Road Level/Formation Level/Road Construction Depth block
    // instead of a units row — confirmed against the real template
    // (public/R&S Take-off sheet.xlsx): Road Level sits at
    // headerRowIdx + 1, not + 2. Using the general +2 "units row" offset
    // here reads Formation Level's cell into `roadLevel`, the calculated
    // Depth formula's cell into `formationLevel`, and the blank buffer row
    // into `depthCell` — silently wrong Road Const Depth on every import,
    // not just when a row shifts the sheet.
    const configRowIdx = headerRowIdx + 1;
    const roadLevelCell = toWs[XLSX.utils.encode_cell({ r: configRowIdx, c: 6 })];
    const formationLevelCell = toWs[XLSX.utils.encode_cell({ r: configRowIdx + 1, c: 6 })];
    const roadLevel = roadLevelCell ? toNum(roadLevelCell.v, 0) : 0;
    const formationLevel = formationLevelCell ? toNum(formationLevelCell.v, 0) : 0;
    // The Road Construction Depth cell itself (one row below Formation Level)
    // — a formula cell (`.f` set) means it's just "= road level − formation
    // level" (Calculated/Auto), so no override is needed; a plain typed-in
    // number (no `.f`) means the tender manually overrode it, matching the
    // modal's own Manual/Auto convention.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const depthCell = toWs[XLSX.utils.encode_cell({ r: configRowIdx + 2, c: 6 })] as any;
    const depthIsManual = !!depthCell && !depthCell.f;
    const depthValue = depthCell ? toNum(depthCell.v, 0) : 0;
    if (roadLevel !== 0 || formationLevel !== 0) {
      importedData.roadConstConfig = {
        roadLevel,
        formationLevel,
        ...(depthIsManual && depthValue !== 0 ? { depth: depthValue } : {}),
      };
    }

    const fullHeaders = ((allRows[headerRowIdx] ?? []) as unknown[]).map((h) => String(h ?? '').trim());

    // Find the column where house laterals start — look for first lateral-specific header
    // that only appears in the right-hand section
    let splitCol = fullHeaders.length;
    for (let i = 5; i < fullHeaders.length; i++) {
      const h = fullHeaders[i].toLowerCase();
      if (h.includes('manhole ref') || h.includes('demarc') || h === 'nme') {
        splitCol = i;
        break;
      }
    }

    const mainHeaders    = fullHeaders.slice(0, splitCol);
    const lateralHeaders = fullHeaders.slice(splitCol);

    const mainSewerRows:    Record<string, unknown>[] = [];
    const houseLateralRows: Record<string, unknown>[] = [];

    // Main Sewer Runs and House Laterals are two column groups describing the
    // SAME physical Excel row — downstream code (combinedFoulTakeOffData,
    // getJunctionQty, etc. in FoulSewersTakeoff.tsx) pairs pipeRuns[i] with
    // laterals[i] by array index, so the two arrays built here must stay in
    // lockstep with the spreadsheet's row order. A row is only dropped when
    // BOTH halves are empty; otherwise both halves are pushed together (the
    // empty half as a blank placeholder) — like a left/right join keyed on
    // row position rather than on any column value.
    // Data starts a few rows below the header (units row, Road Level,
    // Formation Level, Road Construction Depth, then a blank buffer row) -
    // anchored to `headerRowIdx` rather than a hardcoded absolute index for
    // the same reason as above. Starting one row early (at the blank buffer
    // row) is harmless since blank rows are skipped below anyway.
    for (let i = headerRowIdx + 4; i < allRows.length; i++) {
      const row = allRows[i] as unknown[];

      // Left columns → main sewer run. Keep the full row (not just a blank
      // placeholder) even when `hasMain` is false — Excel formula columns
      // like "Avg Depth Category" commonly stay populated on rows where only
      // the MH ref cells are blank, and transformMainSewerRuns reads those
      // through regardless of `_hasMain` (only identity fields are gated).
      const mainObj = Object.fromEntries(
        mainHeaders.map((h, ci) => [h || `col_${ci}`, row[ci] ?? null])
      );
      const hasMain = !isEmptyManholeRef(mainObj['Manhole From']) && !isEmptyManholeRef(mainObj['Manhole To']);
      mainObj['_hasMain'] = hasMain;

      // Right columns → house lateral. Same rationale: keep the full row so
      // transformHouseLaterals can still read demarc/avg-depth formula
      // columns even when there's no real lateral connection on this row.
      // Also carry the same-row 'Backfill' from the main sewer section (col M / idx 12),
      // as the Foul Summary formula references it for the lateral description.
      let latObj: Record<string, unknown> = { _hasLateral: false };
      let hasLateral = false;
      if (lateralHeaders.length > 0) {
        latObj = Object.fromEntries(
          lateralHeaders.map((h, ci) => [h || `col_${splitCol + ci}`, row[splitCol + ci] ?? null])
        );
        // Gate on the Manhole Ref column specifically — not just whichever
        // header happened to trigger splitCol above — so a blank Manhole Ref
        // reliably means "no lateral on this row" regardless of column order.
        hasLateral = !isEmptyManholeRef(col(latObj, 'Manhole Ref (mm)', 'Manhole Ref'));
        latObj['_rowBackfill'] = mainObj['Backfill'] ?? null;
        latObj['_hasLateral'] = hasLateral;
      }

      if (!hasMain && !hasLateral) continue; // genuinely blank spreadsheet row

      mainSewerRows.push(mainObj);
      houseLateralRows.push(latObj);
    }

    importedData.pipeRuns = transformMainSewerRuns(mainSewerRows);
    importedData.laterals = transformHouseLaterals(houseLateralRows);
  } else {
    console.warn("'Foul Take off' sheet not found. Sheets:", workbook.SheetNames);
  }

  // ── 'Pump Station' sheet — section headers in col B (idx 1) or E (idx 4),
  //    quant in col G (idx 6), evaluate code in col I (idx 8) ────────────────
  const psWs = workbook.Sheets['Pump Station'];
  if (psWs) {
    // raw:true (not the formatted string) so parseBuildUpSheet's
    // roundToExcelDisplay sees the true stored double for quantity cells,
    // not a value already mis-rounded by SheetJS's own formatting.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const psRows: unknown[][] = (XLSX as any).utils.sheet_to_json(psWs, {
      header: 1, defval: null, raw: true, blankrows: false,
    });
    const psBuildUp = parseBuildUpSheet(psRows, PUMP_STATION_SECTION_DEFS, PUMP_STATION_DESC_BY_ID, [1, 4], 6, 8);
    importedData.buildUp = { ...importedData.buildUp, ...psBuildUp };
    const psSectionTitles = parsePumpStationSectionTitles(psRows, [1, 4]);
    if (Object.keys(psSectionTitles).length > 0) importedData.pumpStationSectionTitles = psSectionTitles;

    const pumpChamberValues = parseSectionReferenceValues(
      psRows,
      /pump chamber/i,
      PUMP_STATION_SECTION_DEFS.map((sd) => sd.trigger),
      [1, 4],
      3,
      PUMP_CHAMBER_CONFIG_FIELDS,
    );
    // Valve Chamber's Width/Length/Depth aren't separate reference-value
    // cells at all — the sheet's "VALVE CHAMBER" header row (col B, e.g. B65)
    // already holds the fully composed text "VALVE CHAMBER = 67.80M WIDE X
    // 10.77M LONG X 12M DEEP INTERNAL DIMENSIONS" (same convention as the
    // "Wet Well 2100mmØ 4.50m deep" composite description elsewhere on this
    // sheet), so the three numbers are extracted straight out of that string.
    let valveChamberWidth = 0;
    let valveChamberLength = 0;
    let valveChamberDepth = 0;
    for (const row of psRows) {
      const text = String(row[1] ?? '').trim();
      const m = /VALVE CHAMBER\s*=\s*([\d.]+)M\s*WIDE\s*X\s*([\d.]+)M\s*LONG\s*X\s*([\d.]+)M\s*DEEP/i.exec(text);
      if (m) {
        valveChamberWidth = parseFloat(m[1]) || 0;
        valveChamberLength = parseFloat(m[2]) || 0;
        valveChamberDepth = parseFloat(m[3]) || 0;
        break;
      }
    }
    // ps_7's "xxxmm MOT type 1 Sub Base" thickness isn't a separate
    // reference-value cell either — legacy sheets embed it straight in the
    // row's own description text ("150mm Type 1"), same composite-text
    // convention as Valve Chamber's header above, so pull the digits out of
    // whichever row matched that description.
    let motType1SubBaseMm = 0;
    for (const row of psRows) {
      // Item rows carry their description in col E (idx 4) — col B (idx 1) is
      // only ever populated on section-header rows — so E must be checked
      // first; falling back to B here (as the Valve Chamber header parse
      // above does) picked up whatever unrelated value col B held instead.
      const text = (String(row[4] ?? '').trim() || String(row[1] ?? '').trim()).replace(/\s+/g, ' ');
      const m = /^(\d+)\s*mm\s*(?:mot\s*)?type\s*1(?:\s*sub\s*base)?$/i.exec(text);
      if (m) {
        motType1SubBaseMm = parseFloat(m[1]) || 0;
        break;
      }
    }

    // Same composite-text convention for ps_11's "xxxmm thick PAV2 concrete
    // slab" — legacy sheets embed it as "RC30 <mm>mm thk".
    let pav2SlabMm = 0;
    for (const row of psRows) {
      const text = (String(row[4] ?? '').trim() || String(row[1] ?? '').trim()).replace(/\s+/g, ' ');
      const m = /^(?:rc30\s*)?(\d+)\s*mm\s*(?:thk|thick pav2 concrete slab)$/i.exec(text);
      if (m) {
        pav2SlabMm = parseFloat(m[1]) || 0;
        break;
      }
    }

    // ps_10's "xxxx mesh x 2 layers to concrete base only" mesh type — same
    // composite-text convention, embedded as "<mesh code> mesh x 2 layers to
    // concrete base only" (e.g. "B503 mesh x 2 layers to concrete base only").
    let meshType = '';
    for (const row of psRows) {
      const text = (String(row[4] ?? '').trim() || String(row[1] ?? '').trim()).replace(/\s+/g, ' ');
      const m = /^([a-z]\d+) mesh x 2 layers to concrete base only$/i.exec(text);
      if (m) {
        meshType = m[1].toUpperCase();
        break;
      }
    }

    // Kiosk Base's "xxxx mesh" (ps_kb_mesh) — bare "<mesh code> mesh", only
    // ever occurs once on this sheet so a plain scan is safe.
    let kioskBaseMeshType = '';
    for (const row of psRows) {
      const text = (String(row[4] ?? '').trim() || String(row[1] ?? '').trim()).replace(/\s+/g, ' ');
      const m = /^([a-z]\d+) mesh$/i.exec(text);
      if (m) {
        kioskBaseMeshType = m[1].toUpperCase();
        break;
      }
    }

    // Pump Chamber's "xxmm thick concrete blinding" (ps_pc_blinding) — legacy
    // sheets embed it as bare "Blinding" with no thickness; only occurs once.
    let pumpChamberBlindingMm = 0;
    for (const row of psRows) {
      const text = (String(row[4] ?? '').trim() || String(row[1] ?? '').trim()).replace(/\s+/g, ' ');
      const m = /^(\d+)\s*mm thick concrete blinding$/i.exec(text);
      if (m) {
        pumpChamberBlindingMm = parseFloat(m[1]) || 0;
        break;
      }
    }

    // Pump/Valve/Flow Meter Chamber each have their own "2 Layers <mesh code>
    // Mesh to Base" row (ps_pc_mesh/ps_vc_mesh/ps_fm_mesh) with the identical
    // wording repeated once per chamber — unlike the single-occurrence fields
    // above, this can't be a plain whole-sheet scan (it would always grab
    // the first chamber's code for all three), so track which chamber
    // section is currently active the same way parseBuildUpSheet's own
    // section triggers do.
    let pumpChamberMeshType = '';
    let valveChamberMeshType = '';
    let flowMeterChamberMeshType = '';
    // Valve/Flow Meter Chamber's "xxxxx Concrete in Base" concrete grade
    // (ps_vc_concrete/ps_fm_concrete) — no Pump Chamber equivalent exists, so
    // only these two — rides the same active-chamber tracking as the mesh
    // codes above, since the row wording also repeats identically per chamber.
    let valveChamberConcreteGrade = '';
    let flowMeterChamberConcreteGrade = '';
    // Kiosk Base's own "xxxxx Concrete Base" (ps_kb_concrete) uses the exact
    // same "<grade> concrete (in) base" shape as Valve/Flow Meter Chamber's
    // row, so it can't be a plain whole-sheet scan either — it rides this
    // same section-tracking loop rather than a separate one.
    let kioskBaseConcreteGrade = '';
    let activeChamber: 'pump' | 'valve' | 'flowmeter' | 'kiosk' | null = null;
    for (const row of psRows) {
      // Section-header trigger text lives in col B (idx 1) — same [1, 4]
      // priority parseBuildUpSheet's own section triggers use below — while
      // the item row's mesh code is in col E (idx 4), same priority the
      // single-occurrence fields above use. Using col-E-first for the
      // trigger check too (as an earlier version of this loop did) meant any
      // stray non-empty col-E value on the header row itself shadowed the
      // real "PUMP CHAMBER"/"VALVE CHAMBER"/"FLOW METER CHAMBER" text in col
      // B, so the section switch never fired and no chamber matched.
      const headerText = (String(row[1] ?? '').trim() || String(row[4] ?? '').trim()).replace(/\s+/g, ' ');
      const itemText = (String(row[4] ?? '').trim() || String(row[1] ?? '').trim()).replace(/\s+/g, ' ');
      if (!headerText && !itemText) continue;
      if (/pump chamber/i.test(headerText)) { activeChamber = 'pump'; continue; }
      if (/valve chamber/i.test(headerText)) { activeChamber = 'valve'; continue; }
      if (/flow meter chamber/i.test(headerText)) { activeChamber = 'flowmeter'; continue; }
      if (/kiosk base/i.test(headerText)) { activeChamber = 'kiosk'; continue; }
      if (!activeChamber) continue;
      // Pump Chamber's own row reads "2 Layers <code> Mesh to Base"; Valve
      // and Flow Meter Chamber instead read "<code> mesh to concrete base
      // only" (no "2 Layers"/"Mesh to Base") — confirmed against the actual
      // workbook text, not assumed identical across all three chambers.
      const meshMatch = activeChamber === 'pump'
        ? /^2 layers ([a-z]\d+) mesh to base$/i.exec(itemText)
        : /^([a-z]\d+) mesh to concrete base only$/i.exec(itemText);
      if (meshMatch) {
        const code = meshMatch[1].toUpperCase();
        if (activeChamber === 'pump' && !pumpChamberMeshType) pumpChamberMeshType = code;
        else if (activeChamber === 'valve' && !valveChamberMeshType) valveChamberMeshType = code;
        else if (activeChamber === 'flowmeter' && !flowMeterChamberMeshType) flowMeterChamberMeshType = code;
        continue;
      }
      // Valve/Flow Meter Chamber/Kiosk Base's concrete grade — accepts both
      // the current "<grade> Concrete in Base" wording and a bare "<grade>
      // Concrete Base" form, since the real workbook's exact phrasing here
      // isn't confirmed.
      if (activeChamber === 'valve' || activeChamber === 'flowmeter' || activeChamber === 'kiosk') {
        const concreteMatch = /^([a-z0-9]+) concrete(?: in)? base$/i.exec(itemText);
        if (concreteMatch) {
          const grade = concreteMatch[1].toUpperCase();
          if (activeChamber === 'valve' && !valveChamberConcreteGrade) valveChamberConcreteGrade = grade;
          else if (activeChamber === 'flowmeter' && !flowMeterChamberConcreteGrade) flowMeterChamberConcreteGrade = grade;
          else if (activeChamber === 'kiosk' && !kioskBaseConcreteGrade) kioskBaseConcreteGrade = grade;
        }
      }
    }

    if (Object.keys(pumpChamberValues).length > 0 || valveChamberWidth || valveChamberLength || valveChamberDepth || motType1SubBaseMm || pav2SlabMm || meshType || kioskBaseMeshType || pumpChamberBlindingMm || pumpChamberMeshType || valveChamberMeshType || flowMeterChamberMeshType || valveChamberConcreteGrade || flowMeterChamberConcreteGrade || kioskBaseConcreteGrade) {
      importedData.pumpChamberConfig = {
        coverLevel: pumpChamberValues.coverLevel ?? 0,
        invert: pumpChamberValues.invert ?? 0,
        sump: pumpChamberValues.sump ?? 0,
        chamberDia: pumpChamberValues.chamberDia ?? 0,
        valveChamberWidth,
        valveChamberLength,
        valveChamberDepth,
        ...(motType1SubBaseMm ? { motType1SubBaseMm } : {}),
        ...(pav2SlabMm ? { pav2SlabMm } : {}),
        ...(meshType ? { meshType } : {}),
        ...(kioskBaseMeshType ? { kioskBaseMeshType } : {}),
        ...(pumpChamberBlindingMm ? { pumpChamberBlindingMm } : {}),
        ...(pumpChamberMeshType ? { pumpChamberMeshType } : {}),
        ...(valveChamberMeshType ? { valveChamberMeshType } : {}),
        ...(flowMeterChamberMeshType ? { flowMeterChamberMeshType } : {}),
        ...(valveChamberConcreteGrade ? { valveChamberConcreteGrade } : {}),
        ...(flowMeterChamberConcreteGrade ? { flowMeterChamberConcreteGrade } : {}),
        ...(kioskBaseConcreteGrade ? { kioskBaseConcreteGrade } : {}),
      };
    }
  } else {
    console.warn("'Pump Station' sheet not found. Sheets:", workbook.SheetNames);
  }

  // ── 'Rising Main' sheet — description col C (idx 2), quant col D (idx 3), evaluate code col F (idx 5) ──
  const rmWs = workbook.Sheets['Rising Main'];
  if (rmWs) {
    // raw:true — see the 'Pump Station' read above for why.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rmRows: unknown[][] = (XLSX as any).utils.sheet_to_json(rmWs, {
      header: 1, defval: null, raw: true, blankrows: false,
    });
    importedData.buildUp = { ...importedData.buildUp, ...parseBuildUpSheet(rmRows, RISING_MAIN_SECTION_DEFS, RISING_MAIN_DESC_BY_ID, 2, 3, 5) };
  } else {
    console.warn("'Rising Main' sheet not found. Sheets:", workbook.SheetNames);
  }

  // ── 'Foul Summary' sheet — manual EO/Redundant Sewer quantities (col I) ──
  const summaryWs = workbook.Sheets['Foul Summary'];
  if (summaryWs) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const summaryRows: unknown[][] = (XLSX as any).utils.sheet_to_json(summaryWs, {
      header: 1, defval: null, raw: false, blankrows: false,
    });
    const eoQtys = parseFoulSummarySheet(summaryRows);
    if (Object.keys(eoQtys).length > 0) importedData.eoQtys = eoQtys;
  } else {
    console.warn("'Foul Summary' sheet not found. Sheets:", workbook.SheetNames);
  }

  return { data: importedData, errors: [], warnings: [] };
};

// ─── Transformers ──────────────────────────────────────────────────────────

/**
 * Validate imported data for basic consistency
 */
export const validateFoulSewersData = (
  data: FoulSewersExcelData
): {
  isValid: boolean;
  errors: string[];
  warnings: string[];
} => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate manholes
  const manholeRefs = new Set<string>();
  data.manholes.forEach((manhole, index) => {
    if (!manhole.ref || manhole.ref.trim() === "") {
      errors.push(`Manhole ${index + 1}: Reference is required`);
    } else if (manholeRefs.has(manhole.ref)) {
      errors.push(`Manhole ${index + 1}: Duplicate reference "${manhole.ref}"`);
    } else {
      manholeRefs.add(manhole.ref);
    }

    if (manhole.coverLevel <= manhole.invertLevel) {
      errors.push(
        `Manhole ${manhole.ref}: Cover level must be higher than invert level`
      );
    }
  });

  // Validate pipe runs against available manholes
  data.pipeRuns.forEach((pipeRun, index) => {
    if (!pipeRun.fromMH || !manholeRefs.has(pipeRun.fromMH)) {
      warnings.push(
        `Pipe Run ${index + 1}: From MH "${
          pipeRun.fromMH
        }" not found in manholes`
      );
    }
    if (!pipeRun.toMH || !manholeRefs.has(pipeRun.toMH)) {
      warnings.push(
        `Pipe Run ${index + 1}: To MH "${pipeRun.toMH}" not found in manholes`
      );
    }
    if (pipeRun.length <= 0) {
      errors.push(`Pipe Run ${index + 1}: Length must be greater than 0`);
    }
  });

  // Validate laterals against available manholes
  data.laterals.forEach((lateral, index) => {
    if (lateral.connectionType === "direct_to_manhole") {
      if (!lateral.toManhole || !manholeRefs.has(lateral.toManhole)) {
        warnings.push(
          `Lateral ${index + 1}: To Manhole "${
            lateral.toManhole
          }" not found in manholes`
        );
      }
    } else if (lateral.connectionType === "junction_on_run") {
      if (!lateral.toPipeRun) {
        warnings.push(
          `Lateral ${
            index + 1
          }: To Pipe Run is required for junction connections`
        );
      }
    }
    if (lateral.length <= 0) {
      errors.push(`Lateral ${index + 1}: Length must be greater than 0`);
    }
    if (!lateral.ref || lateral.ref.trim() === "") {
      errors.push(`Lateral ${index + 1}: Ref is required`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};
